import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NumberCard } from "../../../components/cards/numberCard";
import { Icon } from "../../../components/icon/icon";
import { CustomDateComponent } from "../../../components/inputs/customDateComponent";
import { Pill } from "../../../components/pill";
import { RevenueStatsChart } from "../../../components/revenue/revenueStatsChart";
import { RevenueTable } from "../../../components/revenue/revenueTable";
import { TableComponent } from "../../../components/table";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useGetAdminDashboardStatsQuery,
  useGetRecentEventsQuery,
} from "../../../redux/features/admin/event/events.api";
import {
  useAdminGetRevenueStatisticsQuery,
  useAdminGetRevenueTableQuery,
} from "../../../redux/features/admin/payments/payments.api";
import { useAppSelector } from "../../../redux/hooks";
import { currencyShorts, eventStatusPillMap } from "../../../utils/constants";
import {
  formatDate,
  formatToCurrency,
  getDisplayRange,
} from "../../../utils/helpers";
import {
  AdminOverviewQueryParams,
  FrequencyFilter,
  QueryParams,
} from "../../../utils/types";

export const OverviewPage = () => {
  const { adminProfile } = useAppSelector((state) => state.auth);
  const [frequency, setFrequency] = useState<FrequencyFilter>("daily");
  const [query, setQuery] = useState<AdminOverviewQueryParams>({
    filter: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [revenueQuery, setRevenueQuery] = useState<QueryParams>({
    page: 1,
    limit: 20,
  });

  const navigate = useNavigate();

  const { isFetching: fetchingRevenueStats, data: revenueStats } =
    useAdminGetRevenueStatisticsQuery(
      {
        filter: frequency,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isLoading: loadingEvents, data: recentEvents } =
    useGetRecentEventsQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const { isFetching: fetchingStats, data: adminStats } =
    useGetAdminDashboardStatsQuery(
      {
        ...query,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingRevenueData, data: revenueData } =
    useAdminGetRevenueTableQuery(
      { ...revenueQuery },
      {
        refetchOnMountOrArgChange: true,
      },
    );
  return (
    <div className="w-full">
      <PageHeader
        header={`Hello ${adminProfile?.firstName}`}
        className="font-unbounded font-normal"
      />
      <BgWrapper className="mt-8 flex w-full flex-col gap-4">
        <div className="flex w-full flex-wrap items-center justify-between gap-1">
          <div className="flex items-center gap-2">
            <Icon svg={"calendar-2"} className="mb-1 text-grey-600" />
            <h1 className="text-base font-bold text-grey-600">
              {getDisplayRange(query.filter, query.startDate, query.endDate)}
            </h1>
          </div>
          <CustomDateComponent
            value={{
              filter: query.filter,
              startDate: query.startDate,
              endDate: query.endDate,
            }}
            onChange={(range) => {
              setQuery((prev) => ({
                ...prev,
                filter: range.filter,
                startDate: range.startDate,
                endDate: range.endDate,
              }));
            }}
          />
        </div>
        <div className="grid w-full grid-cols-1 gap-[10px] md:grid-cols-3">
          <NumberCard
            value={adminStats?.data?.totalOrganisers ?? 0}
            text="Organizers"
            isCurrency={false}
            loading={fetchingStats}
            titleFirst
          />
          <NumberCard
            value={adminStats?.data?.totalEvents ?? 0}
            text="Events"
            isCurrency={false}
            loading={fetchingStats}
            titleFirst
          />
          <NumberCard
            value={adminStats?.data?.totalPaidTickets ?? 0}
            text="Paid Ticket Sold"
            isCurrency={false}
            loading={fetchingStats}
            titleFirst
          />
          <NumberCard
            value={adminStats?.data?.totalGuests ?? 0}
            text="Guest"
            isCurrency={false}
            loading={fetchingStats}
            titleFirst
          />
          <NumberCard
            value={adminStats?.data?.ngnProfit ?? 0}
            text="NG Tikket Profit"
            isCurrency={true}
            loading={fetchingStats}
            titleFirst
          />
          <NumberCard
            value={adminStats?.data?.cadProfit ?? 0}
            text="CAD Tikket Profit"
            isCurrency={true}
            currency="Canadian Dollars"
            loading={fetchingStats}
            titleFirst
          />
        </div>
      </BgWrapper>
      <BgWrapper
        loading={loadingEvents}
        loaderHeight={48}
        className="mt-3 flex w-full items-center justify-between !rounded-b-none border-b border-b-grey-50 py-4"
      >
        <MiniHeader header="Recent Paid Events" />
        <Link to="/events" className="text-base font-bold text-blue-500">
          View All Events
        </Link>
      </BgWrapper>
      <BgWrapper className="mt-[2px] flex w-full flex-col gap-3 !rounded-t-none">
        <TableComponent
          headers={[]}
          rows={
            recentEvents?.data?.map((event, idx) => {
              return {
                id: idx,
                items: [
                  <div
                    className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                  >
                    <img
                      src={event.image}
                      alt="event banner"
                      className="block h-[40px] w-[40px] overflow-hidden rounded-sm object-cover"
                    />

                    <p className="flex flex-1 flex-wrap items-center gap-[2px] text-sm text-grey-600">
                      <span className="font-bold text-grey-900">
                        {event.eventName}
                      </span>
                      <span>
                        {event?.amount > 0
                          ? `∙ From ${formatToCurrency(event.amount, {
                              currency: currencyShorts[event?.currency],
                            })}`
                          : "∙ FREE"}
                      </span>
                      <span>{event?.currency === "NGN" ? " 🇳🇬" : " 🇨🇦 "}</span>
                      <span>{event.isTrending && " 🔥"}</span>
                    </p>
                    <div className="flex flex-row flex-wrap items-center gap-2">
                      <Pill
                        className=""
                        text={eventStatusPillMap[event?.status!]?.text}
                        variation={
                          event.status === "completed"
                            ? "grey"
                            : eventStatusPillMap[event?.status!]?.variant
                        }
                      />
                      <Pill
                        text={`${event.ticketsSold || "NO"} TICKET${event.ticketsSold > 1 ? "S" : ""} SOLD`}
                        variation={"grey"}
                      />
                      <p className="text-sm text-grey-600">
                        On {formatDate(event.startDate, "MMM DD, YYYY")} ∙{" "}
                        {event.startTime}
                      </p>
                    </div>
                  </div>,
                ],
              };
            }) ?? []
          }
          loadingRows={5}
          isLoading={loadingEvents}
          isTableEmpty={[0, undefined].includes(recentEvents?.data?.length)}
          emptyTitle="No Upcoming Events"
          emptySubTitle="Upcoming paid events show here"
          onCTAClick={() => {
            navigate(`/events`);
          }}
          ctaText="View All Events"
          isRowClickable
          onRowClick={(idx) => {
            navigate(`/events/${recentEvents?.data[idx]?._id}`);
          }}
          emptyImage="/assets/images/calendar-3d.png"
          currentPage={1}
          totalCount={recentEvents?.data?.length ?? 0}
          itemsPerPage={5!}
          pageRangeDisplayed={3}
          className="mt-3"
        />
      </BgWrapper>
      <BgWrapper
        loading={fetchingRevenueStats}
        loaderHeight={48}
        className="mt-3 flex w-full items-center justify-between !rounded-b-none border-b border-b-grey-50 py-4"
      >
        <MiniHeader header="Revenue" />
        <Link
          to="/overview/revenue"
          className="text-base font-bold text-blue-500"
        >
          View Revenue
        </Link>
      </BgWrapper>
      <BgWrapper className="mt-[2px] w-full !rounded-none">
        <RevenueStatsChart
          fetchingRevenueStats={fetchingRevenueStats}
          revenueStats={revenueStats}
          frequency={frequency}
          setFrequency={setFrequency}
        />
      </BgWrapper>
      <BgWrapper className="mt-[2px] w-full !rounded-t-none">
        <RevenueTable
          fetchingRevenueData={fetchingRevenueData}
          revenueData={revenueData}
          query={revenueQuery}
          setQuery={setRevenueQuery}
          clickable
        />
      </BgWrapper>
    </div>
  );
};
